import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Clock, AlertTriangle, CheckCircle2, FileCheck, Pencil, List, CalendarDays, ChevronDown, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Pagination } from '@/components/ui/pagination';
import { useDeadlines, useUpdateDeadline, useClients } from '@/hooks/queries';
import { DeadlineStatus } from '@taxistry/types';
import type { Deadline } from '@taxistry/types';

type DeadlineRow_ = Deadline & {
  clientBusinessName?: string;
  clientAfm?: string;
  obligationNameEl?: string;
  obligationNameEn?: string;
  obligationCode?: string;
  obligationFrequency?: string;
};

const STATUS_OPTIONS: { value: DeadlineStatus | ''; label: string }[] = [
  { value: '', label: 'Όλες' },
  { value: DeadlineStatus.PENDING, label: 'Εκκρεμεί' },
  { value: DeadlineStatus.BLOCKED, label: 'Αναμονή Εγγράφων' },
  { value: DeadlineStatus.IN_PROGRESS, label: 'Σε Εξέλιξη' },
  { value: DeadlineStatus.FILED, label: 'Υποβλήθηκε' },
  { value: DeadlineStatus.CONFIRMED, label: 'Επιβεβαιώθηκε' },
  { value: DeadlineStatus.OVERDUE, label: 'Εκπρόθεσμη' },
  { value: DeadlineStatus.SKIPPED, label: 'Παραλείφθηκε' },
];

const WEEKDAY_LABELS = ['Δευ', 'Τρι', 'Τετ', 'Πεμ', 'Παρ', 'Σαβ', 'Κυρ'];
const MONTH_NAMES = [
  'Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος',
  'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος',
];

type ViewMode = 'list' | 'calendar';
type GroupBy = 'none' | 'date' | 'client' | 'obligation';

function getCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday = 0
  const totalDays = lastDay.getDate();
  return { startOffset, totalDays, firstDay, lastDay };
}

export function DeadlinesPage() {
  const { t } = useTranslation();
  const [view, setView] = useState<ViewMode>('list');
  const [statusFilter, setStatusFilter] = useState<DeadlineStatus | ''>('');
  const [clientFilter, setClientFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  const updateDeadline = useUpdateDeadline();
  const [overrideDeadline, setOverrideDeadline] = useState<{ id: string; dueDate: string; overrideNotes: string } | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  // Calendar month/year state
  const now = new Date();
  const [calMonth, setCalMonth] = useState(now.getMonth());
  const [calYear, setCalYear] = useState(now.getFullYear());

  // For calendar view, override date range to the selected month
  const calDateFrom = view === 'calendar' ? `${calYear}-${String(calMonth + 1).padStart(2, '0')}-01` : undefined;
  const calLastDay = view === 'calendar' ? new Date(calYear, calMonth + 1, 0).getDate() : undefined;
  const calDateTo = view === 'calendar' ? `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(calLastDay).padStart(2, '0')}` : undefined;

  const { data, isLoading } = useDeadlines({
    page: view === 'calendar' ? 1 : page,
    limit: view === 'calendar' ? 200 : limit,
    status: (statusFilter || undefined) as DeadlineStatus | undefined,
    clientId: clientFilter || undefined,
    dateFrom: view === 'calendar' ? calDateFrom : (dateFrom || undefined),
    dateTo: view === 'calendar' ? calDateTo : (dateTo || undefined),
    sortBy: 'dueDate',
    sortOrder: 'asc',
  });

  const { data: clientsList } = useClients({ limit: 1000 });

  const today = new Date();

  // Calendar: group deadlines by day number
  const deadlinesByDay = useMemo(() => {
    if (view !== 'calendar' || !data?.data) return {};
    const map: Record<number, typeof data.data> = {};
    for (const dl of data.data) {
      const d = new Date(dl.dueDate);
      if (d.getMonth() === calMonth && d.getFullYear() === calYear) {
        const day = d.getDate();
        if (!map[day]) map[day] = [];
        map[day].push(dl);
      }
    }
    return map;
  }, [view, data, calMonth, calYear]);

  // List view: group deadlines
  const groupedData = useMemo(() => {
    if (view !== 'list' || groupBy === 'none' || !data?.data) return null;
    const groups: Record<string, { label: string; items: typeof data.data }> = {};
    for (const dl of data.data) {
      let key: string;
      let label: string;
      if (groupBy === 'date') {
        key = dl.dueDate.slice(0, 10);
        label = new Date(dl.dueDate).toLocaleDateString('el-GR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      } else if (groupBy === 'client') {
        key = dl.clientId ?? 'unknown';
        label = dl.clientBusinessName ?? '—';
      } else {
        key = dl.obligationId ?? 'unknown';
        label = dl.obligationNameEl ?? dl.obligationCode ?? '—';
      }
      if (!groups[key]) groups[key] = { label, items: [] };
      groups[key].items.push(dl);
    }
    return groups;
  }, [view, groupBy, data]);

  function navigateMonth(delta: number) {
    let m = calMonth + delta;
    let y = calYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setCalMonth(m);
    setCalYear(y);
    setSelectedDay(null);
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-xl font-bold">{t('deadlines.title')}</h1>
        <div className="flex gap-1 border border-border rounded-lg p-0.5">
          <Button
            variant={view === 'list' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => { setView('list'); setSelectedDay(null); }}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={view === 'calendar' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setView('calendar')}
          >
            <CalendarDays className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <Select
              value={statusFilter}
              onValueChange={(v) => { setStatusFilter((v === '__all__' ? '' : v) as DeadlineStatus | ''); setPage(1); }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Κατάσταση" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Όλες</SelectItem>
                {STATUS_OPTIONS.filter((o) => o.value).map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={clientFilter}
              onValueChange={(v) => { setClientFilter(v === '__all__' ? '' : v); setPage(1); }}
            >
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="Πελάτης" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Όλοι οι πελάτες</SelectItem>
                {clientsList?.data.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.businessName}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {view === 'list' && (
              <>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  className="w-[160px]"
                />
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  className="w-[160px]"
                />
                <Select
                  value={groupBy}
                  onValueChange={(v) => setGroupBy(v as GroupBy)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Ομαδοποίηση" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Χωρίς ομαδοποίηση</SelectItem>
                    <SelectItem value="date">Ανά ημερομηνία</SelectItem>
                    <SelectItem value="client">Ανά πελάτη</SelectItem>
                    <SelectItem value="obligation">Ανά υποχρέωση</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Calendar View */}
      {view === 'calendar' && (
        <Card>
          <CardContent className="p-4">
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-4">
              <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="font-display text-lg font-semibold">
                {MONTH_NAMES[calMonth]} {calYear}
              </h2>
              <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {isLoading ? (
              <Skeleton className="h-[320px] w-full" />
            ) : (
              <CalendarGrid
                year={calYear}
                month={calMonth}
                deadlinesByDay={deadlinesByDay}
                today={today}
                selectedDay={selectedDay}
                onSelectDay={setSelectedDay}
              />
            )}

            {/* Selected day deadlines */}
            {selectedDay != null && deadlinesByDay[selectedDay] && (
              <div className="mt-4 border-t pt-4">
                <h3 className="text-sm font-medium mb-2">
                  {selectedDay} {MONTH_NAMES[calMonth]} — {deadlinesByDay[selectedDay].length} προθεσμίες
                </h3>
                <div className="space-y-2">
                  {deadlinesByDay[selectedDay].map((dl) => (
                    <div key={dl.id} className="flex items-center justify-between border border-border rounded-lg px-3 py-2 text-sm">
                      <div>
                        <span className="font-medium">{dl.obligationNameEl ?? dl.obligationCode ?? '—'}</span>
                        <span className="text-muted-foreground ml-2">{dl.clientBusinessName ?? ''}</span>
                      </div>
                      <Badge variant={
                        dl.status === DeadlineStatus.OVERDUE ? 'error'
                        : dl.status === DeadlineStatus.FILED || dl.status === DeadlineStatus.CONFIRMED ? 'success'
                        : dl.status === DeadlineStatus.PENDING ? 'warning'
                        : dl.status === DeadlineStatus.BLOCKED ? 'error'
                        : 'default'
                      }>
                        {t(`deadlines.status.${dl.status}`)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* List View */}
      {view === 'list' && (
        <Card>
          {groupBy !== 'none' && groupedData ? (
            <GroupedDeadlinesView
              groups={groupedData}
              today={today}
              t={t}
              updateDeadline={updateDeadline}
              setOverrideDeadline={setOverrideDeadline}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]"></TableHead>
                    <TableHead>Υποχρέωση</TableHead>
                    <TableHead>Πελάτης</TableHead>
                    <TableHead>Περίοδος</TableHead>
                    <TableHead>{t('deadlines.dueDate')}</TableHead>
                    <TableHead>Κατάσταση</TableHead>
                    <TableHead>Ενέργειες</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 7 }).map((_, j) => (
                          <TableCell key={j}><Skeleton className="h-5 w-full" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : !data?.data.length ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                        {t('deadlines.noDeadlines')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.data.map((dl) => (
                      <DeadlineRow
                        key={dl.id}
                        dl={dl}
                        today={today}
                        t={t}
                        updateDeadline={updateDeadline}
                        setOverrideDeadline={setOverrideDeadline}
                      />
                    ))
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {data && (
                <div className="px-4 py-3 border-t">
                  <Pagination
                    page={data.page}
                    totalPages={data.totalPages}
                    total={data.total}
                    limit={limit}
                    onPageChange={setPage}
                    onLimitChange={setLimit}
                  />
                </div>
              )}
            </>
          )}
        </Card>
      )}

      {/* Override deadline dialog (B-4) */}
      {overrideDeadline && (
        <Dialog open onOpenChange={(open) => !open && setOverrideDeadline(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Τροποποίηση Προθεσμίας</DialogTitle>
              <DialogDescription>Αλλάξτε ημερομηνία ή σημειώσεις</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Νέα ημερομηνία</Label>
                <Input
                  type="date"
                  value={overrideDeadline.dueDate}
                  onChange={(e) => setOverrideDeadline((prev) => prev ? { ...prev, dueDate: e.target.value } : null)}
                />
              </div>
              <div>
                <Label>Σημειώσεις</Label>
                <Textarea
                  value={overrideDeadline.overrideNotes}
                  onChange={(e) => setOverrideDeadline((prev) => prev ? { ...prev, overrideNotes: e.target.value } : null)}
                  placeholder="Λόγος τροποποίησης..."
                  rows={2}
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOverrideDeadline(null)}>
                  Ακύρωση
                </Button>
                <Button
                  onClick={() => {
                    updateDeadline.mutate({
                      id: overrideDeadline.id,
                      data: {
                        dueDate: overrideDeadline.dueDate,
                        overrideNotes: overrideDeadline.overrideNotes || undefined,
                      },
                    });
                    setOverrideDeadline(null);
                  }}
                >
                  Αποθήκευση
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ─── Calendar Grid ──────────────────────────

function CalendarGrid({
  year,
  month,
  deadlinesByDay,
  today,
  selectedDay,
  onSelectDay,
}: {
  year: number;
  month: number;
  deadlinesByDay: Record<number, DeadlineRow_[]>;
  today: Date;
  selectedDay: number | null;
  onSelectDay: (day: number | null) => void;
}) {
  const { startOffset, totalDays } = getCalendarDays(year, month);
  const isCurrentMonth = today.getFullYear() === year && today.getMonth() === month;
  const todayDate = today.getDate();

  const cells: (number | null)[] = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div>
      <div className="grid grid-cols-7 gap-px mb-1">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="text-center text-xs font-medium text-muted-foreground py-1">
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px">
        {cells.map((day, i) => {
          if (day == null) {
            return <div key={`empty-${i}`} className="h-16 bg-muted/20 rounded" />;
          }

          const deadlines = deadlinesByDay[day] ?? [];
          const isToday = isCurrentMonth && day === todayDate;
          const isSelected = day === selectedDay;

          const hasOverdue = deadlines.some((d) => {
            const due = new Date(d.dueDate);
            return d.status === DeadlineStatus.OVERDUE || (due < today && d.status === DeadlineStatus.PENDING);
          });
          const hasUrgent = deadlines.some((d) => {
            const due = new Date(d.dueDate);
            const daysLeft = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            return daysLeft <= 3 && daysLeft >= 0 && d.status === DeadlineStatus.PENDING;
          });
          const hasFiled = deadlines.some((d) => d.status === DeadlineStatus.FILED || d.status === DeadlineStatus.CONFIRMED);

          return (
            <button
              key={day}
              type="button"
              onClick={() => onSelectDay(isSelected ? null : day)}
              className={`h-16 rounded-lg border text-left p-1 transition-colors ${
                isSelected ? 'border-primary bg-primary/5 hover:shadow-sm' :
                isToday ? 'border-primary/50 bg-primary/5 shadow-sm' :
                'border-transparent hover:bg-muted/40 hover:shadow-sm'
              }`}
            >
              <span className={`text-xs font-medium ${isToday ? 'text-primary' : ''}`}>{day}</span>
              {deadlines.length > 0 && (
                <div className="flex gap-0.5 mt-0.5 flex-wrap">
                  {hasOverdue ? <span className="dot red w-2 h-2 rounded-full bg-destructive" /> : null}
                  {hasUrgent ? <span className="dot orange w-2 h-2 rounded-full bg-warning" /> : null}
                  {hasFiled ? <span className="dot green w-2 h-2 rounded-full bg-success" /> : null}
                  {!hasOverdue && !hasUrgent && !hasFiled ? <span className="w-2 h-2 rounded-full bg-muted-foreground" /> : null}
                  {deadlines.length > 1 && (
                    <span className="text-xs text-muted-foreground ml-0.5">{deadlines.length}</span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Deadline Row (shared between ungrouped and grouped views) ──────

function DeadlineRow({
  dl,
  today,
  t,
  updateDeadline,
  setOverrideDeadline,
}: {
  dl: DeadlineRow_;
  today: Date;
  t: (key: string) => string;
  updateDeadline: ReturnType<typeof useUpdateDeadline>;
  setOverrideDeadline: (v: { id: string; dueDate: string; overrideNotes: string } | null) => void;
}) {
  const due = new Date(dl.dueDate);
  const daysLeft = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  const isOverdue = dl.status === DeadlineStatus.OVERDUE || (daysLeft < 0 && dl.status === DeadlineStatus.PENDING);
  const isUrgent = daysLeft <= 3 && daysLeft >= 0 && dl.status === DeadlineStatus.PENDING;

  const statusVariant = dl.status === DeadlineStatus.OVERDUE ? 'error'
    : dl.status === DeadlineStatus.FILED || dl.status === DeadlineStatus.CONFIRMED ? 'success'
    : dl.status === DeadlineStatus.IN_PROGRESS ? 'default'
    : dl.status === DeadlineStatus.BLOCKED ? 'error'
    : dl.status === DeadlineStatus.SKIPPED ? 'secondary'
    : isOverdue ? 'error'
    : dl.status === DeadlineStatus.PENDING ? 'warning'
    : 'secondary';

  const UrgencyIcon = isOverdue ? AlertTriangle
    : dl.status === DeadlineStatus.FILED || dl.status === DeadlineStatus.CONFIRMED ? CheckCircle2
    : dl.status === DeadlineStatus.BLOCKED ? Ban
    : isUrgent ? Clock
    : Clock;

  const urgencyColor = isOverdue ? 'text-red-500'
    : dl.status === DeadlineStatus.FILED || dl.status === DeadlineStatus.CONFIRMED ? 'text-green-500'
    : dl.status === DeadlineStatus.BLOCKED ? 'text-orange-400'
    : isUrgent ? 'text-amber-500'
    : 'text-muted-foreground';

  return (
    <TableRow className={`table-row-hover ${isOverdue ? 'bg-destructive/[0.03]' : isUrgent ? 'bg-amber-50/50' : ''}`}>
      <TableCell>
        <UrgencyIcon className={`h-4 w-4 ${urgencyColor}`} />
      </TableCell>
      <TableCell className="font-medium">{dl.obligationNameEl ?? dl.obligationCode ?? '—'}</TableCell>
      <TableCell>{dl.clientBusinessName ?? '—'}</TableCell>
      <TableCell className="text-muted-foreground">{dl.periodLabel}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <span className={`font-data ${isOverdue ? 'text-red-600 font-medium' : isUrgent ? 'text-amber-600 font-medium' : ''}`}>
            {due.toLocaleDateString('el-GR')}
          </span>
          {dl.status === DeadlineStatus.PENDING && (
            <span className={`inline-flex flex-col items-center justify-center rounded-lg px-2 py-1 font-data text-xs leading-tight min-w-[44px] ${
              isOverdue ? 'bg-red-50 text-red-600' : isUrgent ? 'bg-amber-50 text-amber-600' : 'bg-muted text-muted-foreground'
            }`}>
              <span className="font-bold text-sm">{Math.abs(daysLeft)}</span>
              <span className="text-xs">{daysLeft >= 0 ? 'ημέρες' : 'καθυστ.'}</span>
            </span>
          )}
          {dl.extendedByAdmin ? (
            <Badge variant="outline" className="text-xs py-0">Παράταση</Badge>
          ) : null}
          {dl.overriddenByUser ? (
            <Badge variant="outline" className="text-xs py-0">Τροποπ.</Badge>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant={statusVariant}>{t(`deadlines.status.${dl.status}`)}</Badge>
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          {(dl.status === DeadlineStatus.PENDING || dl.status === DeadlineStatus.IN_PROGRESS || dl.status === DeadlineStatus.BLOCKED) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOverrideDeadline({
                id: dl.id,
                dueDate: new Date(dl.dueDate).toISOString().slice(0, 10),
                overrideNotes: '',
              })}
              title="Τροποποίηση"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {dl.status === DeadlineStatus.PENDING && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateDeadline.mutate({ id: dl.id, data: { status: DeadlineStatus.BLOCKED } })}
                title="Αναμονή Εγγράφων"
              >
                <Ban className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => updateDeadline.mutate({ id: dl.id, data: { status: DeadlineStatus.IN_PROGRESS } })}
                title="Σε Εξέλιξη"
              >
                <Clock className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          {dl.status === DeadlineStatus.BLOCKED && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateDeadline.mutate({ id: dl.id, data: { status: DeadlineStatus.PENDING } })}
              title="Επαναφορά σε Εκκρεμεί"
            >
              <Clock className="h-3.5 w-3.5" />
            </Button>
          )}
          {(dl.status === DeadlineStatus.PENDING || dl.status === DeadlineStatus.IN_PROGRESS) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateDeadline.mutate({ id: dl.id, data: { status: DeadlineStatus.FILED } })}
              title="Υποβλήθηκε"
            >
              <FileCheck className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// ─── Grouped Deadlines View (G-5) ────────────

function GroupedDeadlinesView({
  groups,
  today,
  t,
  updateDeadline,
  setOverrideDeadline,
}: {
  groups: Record<string, { label: string; items: DeadlineRow_[] }>;
  today: Date;
  t: (key: string) => string;
  updateDeadline: ReturnType<typeof useUpdateDeadline>;
  setOverrideDeadline: (v: { id: string; dueDate: string; overrideNotes: string } | null) => void;
}) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  return (
    <div>
      {Object.entries(groups).map(([key, group]) => (
        <div key={key} className="border-b last:border-b-0">
          <button
            type="button"
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors text-left"
            onClick={() => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))}
          >
            <div className="flex items-center gap-2">
              <ChevronDown className={`h-4 w-4 transition-transform ${collapsed[key] ? '-rotate-90' : ''}`} />
              <span className="font-medium text-sm">{group.label}</span>
            </div>
            <Badge variant="secondary" className="text-xs">{group.items.length}</Badge>
          </button>
          {!collapsed[key] && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead>Υποχρέωση</TableHead>
                  <TableHead>Πελάτης</TableHead>
                  <TableHead>Περίοδος</TableHead>
                  <TableHead>{t('deadlines.dueDate')}</TableHead>
                  <TableHead>Κατάσταση</TableHead>
                  <TableHead>Ενέργειες</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {group.items.map((dl) => (
                  <DeadlineRow
                    key={dl.id}
                    dl={dl}
                    today={today}
                    t={t}
                    updateDeadline={updateDeadline}
                    setOverrideDeadline={setOverrideDeadline}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      ))}
    </div>
  );
}
